import {
  ITeacherTransactions,
  ITeacherTransactionsFilters,
} from "api/reactQuery/queries/transaction.types"
import dayjs, { Dayjs } from "dayjs"
import { IClassWithGroups } from "ts/interfaces/Transaction"
import { SenderType } from "ts/types/SenderType"

import type { ClassnameProps, TypeTransactions } from "./AllTransactionsTeacher"
import {
  buildClassFilterChips,
  IClassTreeSelection,
  removeClassFilterChip,
} from "./popoverContent/PopoverContent.utility"

export interface IFiltersDraft {
  typeTransactions: TypeTransactions[]
  classSelection: IClassTreeSelection
  dateFrom: Dayjs | null
  dateTo: Dayjs | null
  senderType: SenderType | ""
}

export const emptyFiltersDraft: IFiltersDraft = {
  typeTransactions: [],
  classSelection: { classIds: [], groupIds: [] },
  dateFrom: null,
  dateTo: null,
  senderType: "",
}

export const isStudentStorePurchase = (
  transaction: Pick<ITeacherTransactions, "teacher" | "helper">
) => transaction.teacher === null && transaction.helper === null

const nonEmpty = <T>(items: T[]) => (items.length ? items : undefined)

const toApiDate = (date: Dayjs | null) =>
  date !== null && date.isValid() ? date.format("YYYY-MM-DD") : undefined

const CHIP_DATE_FORMAT = "MM-DD-YYYY"

export const draftToFilters = (
  draft: IFiltersDraft,
  search: Pick<ITeacherTransactionsFilters, "title_filter" | "student_filter">
): ITeacherTransactionsFilters => ({
  title_filter: search.title_filter || undefined,
  student_filter: search.student_filter || undefined,
  type_in: nonEmpty(draft.typeTransactions),
  date_from: toApiDate(draft.dateFrom),
  date_to: toApiDate(draft.dateTo),
  classes: nonEmpty(draft.classSelection.classIds),
  group_ids: nonEmpty(draft.classSelection.groupIds),
  sender_type: draft.senderType || undefined,
})

export const filtersToDraft = (
  filters: ITeacherTransactionsFilters
): IFiltersDraft => ({
  typeTransactions: filters.type_in ?? [],
  classSelection: {
    classIds: filters.classes ?? [],
    groupIds: filters.group_ids ?? [],
  },
  dateFrom: filters.date_from ? dayjs(filters.date_from) : null,
  dateTo: filters.date_to ? dayjs(filters.date_to) : null,
  senderType: filters.sender_type ?? "",
})

export const hasPopoverFilters = (filters: ITeacherTransactionsFilters) =>
  !!(
    filters.type_in ||
    filters.date_from ||
    filters.date_to ||
    filters.classes ||
    filters.group_ids ||
    filters.sender_type
  )

const formatChipDate = (date: string | undefined) =>
  date ? [dayjs(date).format(CHIP_DATE_FORMAT)] : []

export const buildFilterChips = (
  filters: ITeacherTransactionsFilters,
  classes: IClassWithGroups[],
  senderLabel: (sender: SenderType) => string
): (ClassnameProps | string)[] => [
  ...(filters.sender_type
    ? [
        {
          kind: "sender" as const,
          id: 0,
          name: senderLabel(filters.sender_type),
        },
      ]
    : []),
  ...(filters.type_in ?? []),
  ...buildClassFilterChips(
    { classIds: filters.classes ?? [], groupIds: filters.group_ids ?? [] },
    classes
  ),
  ...formatChipDate(filters.date_from),
  ...formatChipDate(filters.date_to),
]

export const removeFilterChip = (
  draft: IFiltersDraft,
  chip: ClassnameProps | string
): IFiltersDraft => {
  if (typeof chip === "object" && chip.kind === "sender") {
    return { ...draft, senderType: "" }
  }

  if (typeof chip === "object") {
    return {
      ...draft,
      classSelection: removeClassFilterChip(draft.classSelection, chip),
    }
  }

  const isChipDate = (date: Dayjs | null) =>
    date !== null && date.format(CHIP_DATE_FORMAT) === chip

  return {
    ...draft,
    typeTransactions: draft.typeTransactions.filter((type) => type !== chip),
    dateFrom: isChipDate(draft.dateFrom) ? null : draft.dateFrom,
    dateTo: isChipDate(draft.dateTo) ? null : draft.dateTo,
  }
}
